import { createSignal, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  KEY_CAPACITY_MAX,
  KEY_CAPACITY_MIN,
  type KeyCapacityState,
  keyCapacityDraft,
  keyCapacityUsage,
  keyCapacityUseDefaultLabel,
  keyCapacityValueLabel,
  keyCapacityWarning,
  parseKeyCapacity,
} from '../lib/key-capacity';
import { Field } from './Field';
import { Button, buttonClass } from './ui/Button';

export type KeyCapacityProps = {
  /** Scopes the input's id — several cards share one page. */
  readonly keyId: string;
  /** `KeyRow.capacity`: the declared value, or null when the default applies. */
  readonly declared: number | null;
  /** This key's reading of `GET /v1/billing/capacity` (`keyCapacityState`). */
  readonly state: KeyCapacityState;
  /** A save in flight for this key: the Edit control waits for it. */
  readonly busy?: boolean | undefined;
  /** Renders the editor iff passed — the caller decides who may set it (admins). */
  readonly onSave?: ((capacity: number | null) => void) | undefined;
};

const RANGE = { min: KEY_CAPACITY_MIN, max: KEY_CAPACITY_MAX };

/**
 * A key card's "Jobs at once" block (#6863): the set value, the live usage line,
 * the pushback warning, and an inline editor (`KeyPriority`'s shape). Saving is
 * direct, with no confirm. A value the route would refuse never leaves the form;
 * an unchanged one closes without a write — and on a key that follows its
 * default, Save with the prefilled default untouched is unchanged too, so opening
 * the editor can never pin the default by accident. "Use default" sends null.
 */
export function KeyCapacity(props: KeyCapacityProps): JSX.Element {
  const [editing, setEditing] = createSignal(false);
  const [draft, setDraft] = createSignal('');
  const [dirty, setDirty] = createSignal(false);
  const [invalid, setInvalid] = createSignal(false);
  const id = (): string => `key-capacity-${props.keyId}`;
  const usage = (): string | null => keyCapacityUsage(props.state);
  const warning = (): string | null => keyCapacityWarning(props.state);

  const open = (): void => {
    setDraft(keyCapacityDraft(props.declared, props.state));
    setDirty(false);
    setInvalid(false);
    setEditing(true);
  };

  const submit = (): void => {
    const next = parseKeyCapacity(draft());
    if (next === null) {
      setInvalid(true);
      return;
    }
    setEditing(false);
    const changed = props.declared === null ? dirty() : next !== props.declared;
    if (changed) props.onSave?.(next);
  };

  const useDefault = (): void => {
    setEditing(false);
    if (props.declared !== null) props.onSave?.(null);
  };

  return (
    <div class="key-card__capacity">
      <Show
        when={editing() && props.onSave}
        fallback={
          <span class="key-card__capacity-line">
            <span class="key-card__capacity-value">
              {keyCapacityValueLabel(props.declared, props.state)}
            </span>
            <Show when={props.onSave}>
              <Button variant="link" size="sm" disabled={props.busy === true} onClick={open}>
                {t('account_keys.capacity_edit')}
              </Button>
            </Show>
          </span>
        }
      >
        <form
          class="key-card__capacity-form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <Field
            id={id()}
            label={t('account_keys.capacity_label')}
            desc={t('account_keys.capacity_range', RANGE)}
            error={invalid() ? t('account_keys.capacity_invalid', RANGE) : null}
          >
            {(a11y) => (
              <input
                {...a11y}
                class="input"
                type="number"
                inputMode="numeric"
                min={KEY_CAPACITY_MIN}
                max={KEY_CAPACITY_MAX}
                step="1"
                value={draft()}
                onInput={(e) => {
                  setDraft(e.currentTarget.value);
                  setDirty(true);
                  setInvalid(false);
                }}
              />
            )}
          </Field>
          <div class="key-card__capacity-actions">
            <button type="submit" class={buttonClass({ size: 'sm' })}>
              {t('account_keys.capacity_save')}
            </button>
            <Button variant="outline" size="sm" onClick={useDefault}>
              {keyCapacityUseDefaultLabel(props.state)}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              {t('account_keys.cancel')}
            </Button>
          </div>
        </form>
      </Show>
      <Show when={usage()}>{(line) => <span class="key-card__capacity-usage">{line()}</span>}</Show>
      <Show when={warning()}>
        {(text) => <span class="key-card__capacity-warning">{text()}</span>}
      </Show>
    </div>
  );
}
